class SessionStorageService {
    constructor() {
        this.SESSION_KEY = 'makameter_session';
        this.LOCAL_STORAGE_KEY = 'makameter_local_storage';
        this.SESSION_TIMEOUT = 24 * 60 * 60 * 1000; // 24時間
    }

    // セッションデータを保存（sessionStorage使用）
    saveSession(sessionData) {
        try {
            const data = {
                ...sessionData,
                timestamp: Date.now()
            };
            sessionStorage.setItem(this.SESSION_KEY, JSON.stringify(data));
            // バックアップとしてlocalStorageにも保存
            localStorage.setItem(this.SESSION_KEY, JSON.stringify(data));
            return true;
        } catch (error) {
            console.error('セッションデータの保存に失敗しました:', error);
            return false;
        }
    }

    // セッションデータを取得（sessionStorage優先、次にlocalStorage）
    getSession() {
        try {
            // まずsessionStorageから試行
            let data = sessionStorage.getItem(this.SESSION_KEY);
            
            // sessionStorageにない場合、localStorageから取得
            if (!data) {
                data = localStorage.getItem(this.SESSION_KEY);
            }
            
            if (!data) return null;

            const sessionData = JSON.parse(data);
            
            // セッションの有効性をチェック
            if (!this.isValidSession(sessionData)) {
                this.clearSession();
                return null;
            }

            return sessionData;
        } catch (error) {
            console.error('セッションデータの読み込みに失敗しました:', error);
            this.clearSession();
            return null;
        }
    }

    // セッションの有効性を検証（改善版）
    isValidSession(sessionData) {
        if (!sessionData || !sessionData.timestamp) {
            return false;
        }

        // タイムアウトチェック
        const now = Date.now();
        if (now - sessionData.timestamp > this.SESSION_TIMEOUT) {
            return false;
        }

        // 基本的な構造チェック
        if (!sessionData.hasOwnProperty('isRiding')) {
            return false;
        }

        // 乗車中の場合、必要なフィールドをチェック
        if (sessionData.isRiding) {
            if (!sessionData.currentRide || !sessionData.startTime) {
                return false;
            }
        }

        return true;
    }

    // セッションを更新
    updateSession(updates) {
        const currentSession = this.getSession();
        if (!currentSession) return false;

        const updatedSession = {
            ...currentSession,
            ...updates,
            timestamp: Date.now()
        };

        return this.saveSession(updatedSession);
    }

    // 特定のフィールドを更新
    updateField(fieldName, value) {
        const updates = {};
        updates[fieldName] = value;
        return this.updateSession(updates);
    }

    // セッションをクリア（sessionStorageとlocalStorageの両方）
    clearSession() {
        try {
            sessionStorage.removeItem(this.SESSION_KEY);
            localStorage.removeItem(this.SESSION_KEY);
            return true;
        } catch (error) {
            console.error('セッションクリアに失敗しました:', error);
            return false;
        }
    }

    // 乗車セッションを開始
    startRideSession(rideData) {
        const sessionData = {
            isRiding: true,
            currentRide: rideData.ride,
            currentLocation: rideData.startLocation,
            route: [rideData.startLocation],
            totalDistance: 0,
            currentFare: 620,
            elapsedTime: 0,
            startTime: Date.now()
        };

        return this.saveSession(sessionData);
    }

    // ルートを更新
    updateRoute(newLocation) {
        const session = this.getSession();
        if (!session) return false;

        const updatedRoute = [...session.route, newLocation];
        
        return this.updateSession({
            currentLocation: newLocation,
            route: updatedRoute
        });
    }

    // 距離と料金を更新
    updateDistanceAndFare(distance, fare) {
        return this.updateSession({
            totalDistance: distance,
            currentFare: fare
        });
    }

    // 経過時間を更新
    updateElapsedTime(elapsedTime) {
        return this.updateField('elapsedTime', elapsedTime);
    }

    // 乗車を終了
    endRideSession() {
        return this.clearSession();
    }

    // デバッグ用: セッション情報を出力
    debugSession() {
        const session = this.getSession();
        if (session) {
            console.log('Current Session:', {
                isRiding: session.isRiding,
                rideId: session.currentRide?.id,
                startTime: session.startTime ? new Date(session.startTime).toLocaleString() : 'N/A',
                elapsedTime: session.elapsedTime,
                totalDistance: session.totalDistance,
                currentFare: session.currentFare,
                routePoints: session.route?.length || 0,
                currentLocation: session.currentLocation ? 
                    `${session.currentLocation.lat.toFixed(6)}, ${session.currentLocation.lng.toFixed(6)}` : 'N/A',
                timestamp: new Date(session.timestamp).toLocaleString(),
                storageType: sessionStorage.getItem(this.SESSION_KEY) ? 'sessionStorage' : 'localStorage'
            });
        } else {
            console.log('No active session found');
        }
    }

    // セッションの存在をチェック
    hasActiveSession() {
        return this.getSession() !== null;
    }

    // セッションの完全性をチェック
    validateSessionIntegrity() {
        const session = this.getSession();
        if (!session) return false;

        const requiredFields = ['isRiding', 'timestamp'];
        for (const field of requiredFields) {
            if (!session.hasOwnProperty(field)) {
                console.warn(`Session missing required field: ${field}`);
                return false;
            }
        }

        if (session.isRiding) {
            const ridingRequiredFields = ['currentRide', 'startTime', 'route'];
            for (const field of ridingRequiredFields) {
                if (!session.hasOwnProperty(field)) {
                    console.warn(`Active ride session missing required field: ${field}`);
                    return false;
                }
            }
        }

        return true;
    }
}

export default new SessionStorageService();